import QRCode from 'qrcode';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Logo } from '../../components/Logo';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useAuth, type SignInChallenge } from './auth';
import './login.css';

/**
 * Sign in, in the console's own design.
 *
 * The Cognito Hosted UI handled the whole challenge chain for free; doing it here means
 * owning each step — a temporary password from an invite, first-time TOTP enrolment, and the
 * per-sign-in code. `AuthProvider` models those as a union so this page only has to render
 * whichever one is outstanding.
 */

const COPY: Record<SignInChallenge['kind'], { title: string; sub: string; cta: string }> = {
  none: { title: 'Sign in', sub: 'Continue to your security twin.', cta: 'Sign in' },
  newPassword: {
    title: 'Choose a password',
    sub: 'Your invite used a temporary password. Pick a permanent one to continue.',
    cta: 'Set password',
  },
  totpSetup: {
    title: 'Set up authentication',
    sub: 'Scan this with your authenticator app, then enter the six-digit code it shows.',
    cta: 'Verify and continue',
  },
  totpCode: {
    title: 'Enter your code',
    sub: 'Open your authenticator app and enter the current six-digit code.',
    cta: 'Verify',
  },
};

function TotpQr({ uri }: { uri: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvas.current) return;
    // Rendered locally: the shared secret must never leave the browser for a QR service.
    void QRCode.toCanvas(canvas.current, uri, { width: 176, margin: 1 }).catch(() => {
      // The secret is shown as text below, so enrolment still works without the QR.
    });
  }, [uri]);

  return <canvas ref={canvas} className="login-qr" aria-label="Authenticator setup QR code" />;
}

export function LoginPage() {
  const { signIn, respond, status, challenge, error: authError } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [answer, setAnswer] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/app';
  if (status === 'signedIn') return <Navigate to={from} replace />;

  const copy = COPY[challenge.kind];
  const isCredentials = challenge.kind === 'none';

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      setAnswer('');
      setConfirm('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (isCredentials) {
      void run(() => signIn(email.trim(), password));
      return;
    }

    if (challenge.kind === 'newPassword') {
      if (answer !== confirm) {
        setError('Those passwords do not match.');
        return;
      }
      void run(() => respond(answer));
      return;
    }

    void run(() => respond(answer.trim()));
  };

  return (
    <div className="login">
      {/* Decorative only: everything actionable lives in the pane. */}
      <section className="login-aside" aria-hidden="true">
        <div className="login-aside-grid" />
        <div className="login-aside-brand">
          <Logo />
        </div>
        <div className="login-aside-copy">
          <h1 className="serif">
            Know your exposure <span className="accent-italic">before</span> someone else does.
          </h1>
          <p>
            Agent-driven application security testing, with every finding tied to evidence and every
            intrusive step approved by a person.
          </p>
        </div>
        <dl className="login-aside-stats">
          <div>
            <dt>Scope</dt>
            <dd>immutable, versioned</dd>
          </div>
          <div>
            <dt>Approvals</dt>
            <dd>recorded per gate</dd>
          </div>
          <div>
            <dt>Evidence</dt>
            <dd>request and response</dd>
          </div>
        </dl>
      </section>

      <section className="login-pane">
        <header className="login-pane-top">
          <div className="login-pane-brand">
            <Logo />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ThemeToggle />
            <Link to="/" className="muted" style={{ fontSize: 14 }}>
              ← Back to site
            </Link>
          </div>
        </header>

        <div className="login-form-wrap">
          <form className="login-form" onSubmit={onSubmit} aria-labelledby="login-title">
            <h2 id="login-title" className="serif">
              {copy.title}
            </h2>
            <p className="muted login-sub">{copy.sub}</p>

            {(error ?? authError) ? (
              <p className="login-error" role="alert">
                {error ?? authError}
              </p>
            ) : null}

            {isCredentials ? (
              <>
                <div className="field">
                  <label htmlFor="email">Work email</label>
                  <input
                    id="email"
                    className="input"
                    type="email"
                    autoComplete="username"
                    required
                    placeholder="name@institution.com.au"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            ) : null}

            {challenge.kind === 'newPassword' ? (
              <>
                <div className="field">
                  <label htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={12}
                    placeholder="At least 12 characters"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="confirm-password">Confirm password</label>
                  <input
                    id="confirm-password"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={12}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
              </>
            ) : null}

            {challenge.kind === 'totpSetup' ? (
              <div className="login-totp">
                <TotpQr uri={challenge.otpauthUri} />
                <div className="login-totp-secret">
                  <span className="muted">Can’t scan? Enter this key instead:</span>
                  <code>{challenge.secret}</code>
                </div>
              </div>
            ) : null}

            {challenge.kind === 'totpSetup' || challenge.kind === 'totpCode' ? (
              <div className="field">
                <label htmlFor="code">Six-digit code</label>
                <input
                  id="code"
                  className="input login-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  placeholder="000000"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? 'Working…' : copy.cta}
            </button>

            <p className="muted login-note">
              Multi-factor authentication is required on every account.
            </p>
          </form>

          <footer className="login-foot">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="dot" />
              Authorised users only · all activity is recorded
            </span>
          </footer>
        </div>
      </section>
    </div>
  );
}
